import {
  useFetchCompanyUserDataQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} from "api/usersApi";
import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  CardText,
} from "reactstrap";
import { getApiErrorMessage } from "../api/errorUtils";
import { useTranslation } from "../hooks/useTranslation";

/** Mirrors backend `ChangePasswordDto` / `PASSWORD_PATTERN` for client-side checks */
const NEW_PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,128}$/;

function PasswordField({
  id,
  name,
  label,
  value,
  onChange,
  disabled,
  show,
  onToggleShow,
  autoComplete,
  t,
}) {
  return (
    <FormGroup>
      <label className="text-sm font-weight-bold mb-1 d-block" htmlFor={id}>
        {label}
      </label>
      <div className="d-flex align-items-stretch" style={{ gap: 8 }}>
        <Input
          id={id}
          name={name}
          type={show ? "text" : "password"}
          autoComplete={autoComplete || "off"}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        />
        <Button
          type="button"
          color="secondary"
          outline
          size="sm"
          className="align-self-stretch"
          style={{ flexShrink: 0, minWidth: "4.5rem" }}
          onClick={onToggleShow}
          disabled={disabled}
        >
          {show ? t("userProfilePage.hidePassword") : t("userProfilePage.showPassword")}
        </Button>
      </div>
    </FormGroup>
  );
}

function UserProfile() {
  const t = useTranslation();
  const [userData, setUserData] = useState({ user: {}, company: {} });
  const {
    data: userDataResponse,
    error,
    isLoading,
  } = useFetchCompanyUserDataQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();

  const [formState, setFormState] = useState({
    username: "",
    surname: "",
    email: "",
  });

  const [passwordState, setPasswordState] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({});

  const [profileAlert, setProfileAlert] = useState(null);
  const [passwordAlert, setPasswordAlert] = useState(null);

  const clearPasswordAlerts = useCallback(() => {
    setPasswordAlert(null);
    setPasswordFieldErrors({});
  }, []);

  useEffect(() => {
    if (userDataResponse) {
      setUserData(userDataResponse);
      setFormState({
        username: userDataResponse.user?.username || "",
        surname: userDataResponse.user?.surname || "",
        email: userDataResponse.user?.email || "",
      });
    }
  }, [userDataResponse]);

  const { user = {}, company = {} } = userData || {};

  const handleInputChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
    setProfileAlert(null);
  };

  const handlePasswordInputChange = ({ target: { name, value } }) => {
    setPasswordState((prev) => ({ ...prev, [name]: value }));
    clearPasswordAlerts();
  };

  const handleSubmitProfile = async (event) => {
    event.preventDefault();
    setProfileAlert(null);
    try {
      const updatedUser = await updateProfile(formState).unwrap();
      setUserData((prev) => ({
        ...prev,
        user: updatedUser?.data || updatedUser || prev.user,
      }));
      setProfileAlert({
        type: "success",
        message: t("userProfilePage.profileUpdated"),
      });
    } catch (submitError) {
      setProfileAlert({
        type: "danger",
        message: getApiErrorMessage(submitError, "Failed to update profile.", {
          409: "That email address is already in use by another account.",
        }),
      });
    }
  };

  const validatePasswordClient = () => {
    const errs = {};
    if (!passwordState.currentPassword.trim()) {
      errs.currentPassword = t("userProfilePage.currentPasswordRequired");
    }
    if (!passwordState.newPassword) {
      errs.newPassword = t("userProfilePage.newPassword");
    } else if (!NEW_PASSWORD_REGEX.test(passwordState.newPassword)) {
      errs.newPassword = t("userProfilePage.passwordRequirements");
    }
    if (passwordState.newPassword !== passwordState.confirmPassword) {
      errs.confirmPassword = t("userProfilePage.passwordsMustMatch");
    }
    setPasswordFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmitPassword = async (event) => {
    event.preventDefault();
    clearPasswordAlerts();
    if (!validatePasswordClient()) {
      return;
    }
    try {
      await changePassword({
        currentPassword: passwordState.currentPassword,
        newPassword: passwordState.newPassword,
      }).unwrap();
      setPasswordState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setPasswordAlert({
        type: "success",
        message: t("userProfilePage.passwordChanged"),
      });
    } catch (err) {
      setPasswordAlert({
        type: "danger",
        message: getApiErrorMessage(err, "Unable to update password.", {
          401: "Current password is incorrect.",
          403:
            "Password change is not available for this account. Use your usual sign-in method or contact an administrator.",
          400: "Please check the password requirements and try again.",
        }),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="content">
        <p className="text-muted mb-0">Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="content">
        <Alert color="danger">{getApiErrorMessage(error, "Unable to load profile")}</Alert>
      </div>
    );
  }

  return (
    <div className="content profile-page-cms">
      <Row>
        <Col lg="8" md="12">
          {profileAlert ? (
            <Alert color={profileAlert.type} className="mb-3" toggle={() => setProfileAlert(null)}>
              {profileAlert.message}
            </Alert>
          ) : null}

          <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: 10 }}>
            <CardHeader
              style={{
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                paddingBottom: 16,
              }}
            >
              <h5 className="mb-1" style={{ fontWeight: 600, letterSpacing: "-0.02em" }}>
                {t("userProfilePage.editProfileTitle")}
              </h5>
              <p className="text-muted mb-0 small">
                {company.name ? `${company.name} · ` : ""}
                {user.role || ""}
              </p>
            </CardHeader>
            <CardBody style={{ paddingTop: 24 }}>
              <Form onSubmit={handleSubmitProfile}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label className="text-sm font-weight-bold mb-1 d-block">Username</label>
                      <Input
                        value={formState.username}
                        name="username"
                        onChange={handleInputChange}
                        placeholder="Username"
                        type="text"
                        disabled={isSaving}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="text-sm font-weight-bold mb-1 d-block">Email</label>
                      <Input
                        value={formState.email}
                        name="email"
                        onChange={handleInputChange}
                        type="email"
                        disabled={isSaving}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label className="text-sm font-weight-bold mb-1 d-block">Surname</label>
                      <Input
                        value={formState.surname}
                        name="surname"
                        onChange={handleInputChange}
                        placeholder="Surname"
                        type="text"
                        disabled={isSaving}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="text-sm font-weight-bold mb-1 d-block">Role</label>
                      <Input
                        defaultValue={user.role || ""}
                        placeholder="Role"
                        type="text"
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label className="text-sm font-weight-bold mb-1 d-block">Company</label>
                      <Input defaultValue={company.name || ""} disabled type="text" />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="text-sm font-weight-bold mb-1 d-block">Registered</label>
                      <Input
                        defaultValue={user.created_at?.slice(0, 10) || ""}
                        disabled
                        type="text"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </CardBody>
            <CardFooter
              className="bg-transparent border-0 d-flex justify-content-end pt-0"
              style={{ paddingTop: 0 }}
            >
              <Button color="primary" type="submit" disabled={isSaving} onClick={handleSubmitProfile}>
                {isSaving ? t("userProfilePage.saving") : t("userProfilePage.saveProfile")}
              </Button>
            </CardFooter>
          </Card>

          <Card className="shadow-sm border-0" style={{ borderRadius: 10 }}>
            <CardHeader
              style={{
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                paddingBottom: 16,
              }}
            >
              <h5 className="mb-1" style={{ fontWeight: 600, letterSpacing: "-0.02em" }}>
                {t("userProfilePage.securityTitle")}
              </h5>
              <p className="text-muted mb-0 small">{t("userProfilePage.changePasswordSubtitle")}</p>
            </CardHeader>
            <CardBody style={{ paddingTop: 20 }}>
              {passwordAlert ? (
                <Alert
                  color={passwordAlert.type}
                  className="mb-3"
                  toggle={() => setPasswordAlert(null)}
                >
                  {passwordAlert.message}
                </Alert>
              ) : null}

              <Form onSubmit={handleSubmitPassword}>
                <PasswordField
                  id="profile-current-password"
                  name="currentPassword"
                  label={`${t("userProfilePage.currentPassword")} *`}
                  value={passwordState.currentPassword}
                  onChange={handlePasswordInputChange}
                  disabled={isChangingPassword}
                  show={showCurrent}
                  onToggleShow={() => setShowCurrent((v) => !v)}
                  autoComplete="current-password"
                  t={t}
                />
                {passwordFieldErrors.currentPassword ? (
                  <div className="text-danger small mb-2">{passwordFieldErrors.currentPassword}</div>
                ) : null}

                <PasswordField
                  id="profile-new-password"
                  name="newPassword"
                  label={`${t("userProfilePage.newPassword")} *`}
                  value={passwordState.newPassword}
                  onChange={handlePasswordInputChange}
                  disabled={isChangingPassword}
                  show={showNew}
                  onToggleShow={() => setShowNew((v) => !v)}
                  autoComplete="new-password"
                  t={t}
                />
                {passwordFieldErrors.newPassword ? (
                  <div className="text-danger small mb-2">{passwordFieldErrors.newPassword}</div>
                ) : (
                  <p className="text-muted small mb-3">{t("userProfilePage.passwordRequirements")}</p>
                )}

                <PasswordField
                  id="profile-confirm-password"
                  name="confirmPassword"
                  label={`${t("userProfilePage.confirmPassword")} *`}
                  value={passwordState.confirmPassword}
                  onChange={handlePasswordInputChange}
                  disabled={isChangingPassword}
                  show={showConfirm}
                  onToggleShow={() => setShowConfirm((v) => !v)}
                  autoComplete="new-password"
                  t={t}
                />
                {passwordFieldErrors.confirmPassword ? (
                  <div className="text-danger small mb-2">{passwordFieldErrors.confirmPassword}</div>
                ) : null}
              </Form>
            </CardBody>
            <CardFooter className="bg-transparent border-0 d-flex justify-content-end pt-0">
              <Button
                color="primary"
                outline={false}
                type="submit"
                disabled={isChangingPassword}
                onClick={handleSubmitPassword}
              >
                {isChangingPassword
                  ? t("userProfilePage.saving")
                  : t("userProfilePage.updatePassword")}
              </Button>
            </CardFooter>
          </Card>
        </Col>

        <Col lg="4" md="12">
          <Card className="card-user shadow-sm border-0" style={{ borderRadius: 10 }}>
            <CardBody>
              <CardText />
              <div className="author text-center">
                <div className="block block-one" />
                <div className="block block-two" />
                <div className="block block-three" />
                <div className="block block-four" />
                <img
                  alt=""
                  className="avatar mx-auto d-block"
                  src={require("assets/img/emilyz.jpg")}
                  style={{ maxWidth: 96, borderRadius: "50%" }}
                />
                <h5 className="title mt-3 mb-1" style={{ fontWeight: 600 }}>
                  {user.username} {user.surname}
                </h5>
                <p className="description text-muted small mb-0">
                  {company.name} — {user.role}
                </p>
              </div>
              <p className="description text-center text-muted small mt-3 mb-0">
                Manage your profile and password from this page.
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default UserProfile;
